/* eslint-disable */
import React, { useState, useEffect, useRef } from 'react';
import { Container, Spinner, Modal, Button } from 'react-bootstrap';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

import Login from './components/Login';
import VerifyCode from './components/VerifyCode';
import QuestionnaireTable from './components/QuestionnaireTable';
import QuestionnaireView from './components/QuestionnaireView';
import QuestionnairePreview from './components/QuestionnairePreview';
import MaintenancePage from './components/MaintenancePage';
import { AppInsightsProvider } from './lib/AppInsightsProvider';
import { trackEvent } from './lib/appInsights';

function App() {
    const [view, setView] = useState('loading');
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [jwt, setJwt] = useState(null);
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [maintenanceMode, setMaintenanceMode] = useState(false);
    const [appView, setAppView] = useState('table');
    const [activeQuestionnaireId, setActiveQuestionnaireId] = useState(null);
    const [termsUrl, setTermsUrl] = useState('');
    const [isReadOnly, setIsReadOnly] = useState(false);
    const [isDynamicsMode, setIsDynamicsMode] = useState(false);
    const [isPortalMode, setIsPortalMode] = useState(false);
    const [isPreviewMode, setIsPreviewMode] = useState(false);
    const [previewVersionId, setPreviewVersionId] = useState(null);
    const [includeDraft, setIncludeDraft] = useState(true);
    const [wafTokenMap, setWafTokenMap] = useState(null);
    const [resendToken, setResendToken] = useState(null);
    const [recaptchaSiteKey, setRecaptchaSiteKey] = useState('');
    const [appInsightsConnectionString, setAppInsightsConnectionString] = useState('');


    const [showExpiryModal, setShowExpiryModal] = useState(false);
    const [countdown, setCountdown] = useState(120);

    // useRef to store timer IDs so they persist across re-renders
    const warningTimer = useRef(null);
    const logoutTimer = useRef(null);
    const countdownTimer = useRef(null);
    const autoRefreshRef = useRef(null);
    const maintenancePollRef = useRef(null);

    // --- Session Timer Management ---
    const clearSessionTimers = () => {
        clearTimeout(warningTimer.current);
        clearTimeout(logoutTimer.current);
    };

    // Derived: true when embedded in any trusted host (Dynamics or Portal)
    const isEmbeddedMode = isDynamicsMode || isPortalMode;

    // Derived telemetry dimension describing how the user is authenticating.
    const authMode = isDynamicsMode ? 'dynamics' : isPortalMode ? 'portal' : 'anonymous';

    const startSessionTimers = (token, isEmbedded) => {
        clearSessionTimers();

        try {
            const decodedToken = jwtDecode(token);
            const currentTime = Date.now() / 1000;
            const expiryTime = decodedToken.exp;

            const timeUntilExpiry = (expiryTime - currentTime) * 1000;
            const timeUntilWarning = timeUntilExpiry - 120000; //(2 minutes)


            if (timeUntilWarning > 0) {
                if (isEmbedded) {

                    console.log(`Embedded mode: Scheduling auto-refresh in ${Math.round(timeUntilWarning / 1000)}s`);
                    warningTimer.current = setTimeout(() => {
                        if (autoRefreshRef.current) {
                            autoRefreshRef.current();
                        }
                    }, timeUntilWarning);
                } else {
                    warningTimer.current = setTimeout(() => {
                        setShowExpiryModal(true);
                    }, timeUntilWarning);
                }
            }

            if (timeUntilExpiry > 0) {
                // In embedded mode we rely on auto-refresh, so we don't set a hard logout timer
                // If refresh fails, that logic will handle logout
                if (!isEmbedded) {
                    logoutTimer.current = setTimeout(() => {
                        console.log("Session expired. Logging out.");
                        handleLogout(true);
                    }, timeUntilExpiry);
                }
            } else {
                handleLogout(true);
            }
        } catch (e) {
            console.error("Invalid token:", e);
            handleLogout();
        }
    };

    const handleExtendSession = async () => {
        setShowExpiryModal(false);
        try {
            const api = axios.create({ headers: { Authorization: `Bearer ${jwt}` } });
            const response = await api.post('/api/refreshToken');
            const newToken = response.data.token;

            setJwt(newToken);
            startSessionTimers(newToken); // Restart the timers with the new token
            trackEvent('SessionExtended', { authMode });
            console.log("Session extended successfully.");
        } catch (err) {
            console.error("Failed to extend session:", err);
            alert("Could not extend your session. Please log in again.");
            handleLogout(true);
        }
    };

    useEffect(() => {
        if (showExpiryModal) {
            setCountdown(120);

            countdownTimer.current = setInterval(() => {
                setCountdown(prevCountdown => {
                    if (prevCountdown <= 1) {
                        clearInterval(countdownTimer.current);
                        return 0;
                    }
                    return prevCountdown - 1;
                });
            }, 1000);
        }
        return () => clearInterval(countdownTimer.current);
    }, [showExpiryModal]);


    const setAuthenticatedSession = (token, isEmbedded) => {
        setJwt(token);
        setIsAuthenticated(true);
        setView('authenticated');

        trackEvent('SessionStarted', { authMode, embedded: !!isEmbedded });

        // Fetch WAF token map now that we have a JWT
        axios.get('/api/GetAppConfig/Isoqar.Xait.Questionnaire.WAFTokenMap', {
            headers: { Authorization: `Bearer ${token}` }
        }).then(response => {
            const map = response.data?.value ? JSON.parse(response.data.value) : null;
            if (Array.isArray(map)) {
                setWafTokenMap(map);
            }
        }).catch(err => {
            console.error('Failed to fetch WAF token map:', err.message);
        });

        // Pass the 'isEmbedded' flag directly
        startSessionTimers(token, isEmbedded);
    };


    useEffect(() => {
        const authenticateWithEmail = async (email, questionnaireId, readOnly, dynamicsModeFlag, portalModeFlag, signature, timestamp, viewerEmail) => {
            setError(null);
            setView('loading');
            try {
                const payload = { email: email };
                if (signature) payload.signature = signature;
                if (timestamp) payload.timestamp = parseInt(timestamp, 10);
                if (viewerEmail) payload.viewerEmail = viewerEmail;

                if (portalModeFlag) payload.source = 'portal';
                const response = await axios.post('/api/issueSessionToken', payload);

                if (dynamicsModeFlag) setIsDynamicsMode(true);
                if (portalModeFlag) setIsPortalMode(true);

                setAuthenticatedSession(response.data.token, dynamicsModeFlag || portalModeFlag);

                if (questionnaireId) {
                    console.log(`Directly loading questionnaire: ${questionnaireId}`);
                    setActiveQuestionnaireId(questionnaireId);
                    setIsReadOnly(readOnly);
                    setAppView('survey');
                }
            } catch (err) {
                setError('Session could not be started via the link. Please try logging in manually.');
                setView('login');
            }
        };

        // Auto-authenticate for preview mode with email parameter
        const authenticateForPreview = async (email, versionId, signature, timestamp, viewerEmail) => {
            setError(null);
            setView('loading');
            try {
                const payload = { email: email };
                if (signature) payload.signature = signature;
                if (timestamp) payload.timestamp = parseInt(timestamp, 10);
                if (viewerEmail) payload.viewerEmail = viewerEmail;

                const response = await axios.post('/api/issueSessionToken', payload);

                setIsDynamicsMode(true); // Preview in Dynamics context
                setPreviewVersionId(versionId);
                setIsPreviewMode(true);
                setAuthenticatedSession(response.data.token, true);

                console.log(`Auto-authenticated for preview: ${versionId}`);
            } catch (err) {
                setError('Session could not be started via the link. Please try logging in manually.');
                setPreviewVersionId(versionId);
                setIsPreviewMode(true);
                setView('login');
            }
        };

        const fetchConfig = async () => {
            try {
                const [termsResponse, configResponse] = await Promise.all([
                    axios.get('/api/getTermsAndConditions'),
                    axios.get('/api/config')
                ]);
                setTermsUrl(termsResponse.data.termsAndConditionsUrl);
                setRecaptchaSiteKey(configResponse.data.recaptchaSiteKey || '');
                setAppInsightsConnectionString(configResponse.data.appInsightsConnectionString || '');

                const isMaintenance = configResponse.data.maintenanceMode === true;
                setMaintenanceMode(isMaintenance);

                if (isMaintenance) return;

                const params = new URLSearchParams(window.location.search);
                const emailParam = params.get('email');
                const questionnaireIdParam = params.get('questionnaireid');
                const readOnlyParam = params.get('readonly');
                const dynamicsModeParam = params.get('dynamicsMode');
                const portalModeParam = params.get('portalMode');
                const signatureParam = params.get('signature');
                const timestampParam = params.get('timestamp');
                const viewerEmailParam = params.get('viewerEmail');
                const isDynamicsFlag = dynamicsModeParam === 'true';
                const isPortalFlag = portalModeParam === 'true';
                const includeDraftParam = params.get('includeDraft');

                // Check if this is the questionnaire-preview route
                const isPreviewRoute = window.location.pathname.includes('/questionnaire-preview');
                const previewVersionIdParam = params.get('questionnaireVersionId');

                if (isPreviewRoute && previewVersionIdParam) {
                    // Set includeDraft from URL parameter (defaults to true if not specified)
                    setIncludeDraft(includeDraftParam === 'false' ? false : true);

                    // Preview mode - check if email is provided for auto-authentication
                    if (emailParam) {
                        // Auto-authenticate with email (for Dynamics 365 embedding)
                        authenticateForPreview(emailParam, previewVersionIdParam, signatureParam, timestampParam, viewerEmailParam);
                    } else {
                        // No email - require manual login
                        setPreviewVersionId(previewVersionIdParam);
                        setIsPreviewMode(true);
                        setView('login');
                    }
                } else if (emailParam) {
                    authenticateWithEmail(emailParam, isPortalFlag ? null : questionnaireIdParam, !!readOnlyParam, isDynamicsFlag, isPortalFlag, signatureParam, timestampParam, viewerEmailParam);
                } else if (questionnaireIdParam) {
                    setActiveQuestionnaireId(questionnaireIdParam);
                    setIsReadOnly(!!readOnlyParam);
                    setView('login');
                } else {
                    setView('login');
                }
            } catch (err) {
                console.error("Failed to fetch app configuration:", err);
                // If config fetch fails, fall through to normal login flow
                setView('login');
            }
        };

        fetchConfig();
    }, []);

    useEffect(() => {
        const pollMaintenance = async () => {
            try {
                const response = await axios.get('/api/config');
                if (response.data.maintenanceMode === true) {
                    setMaintenanceMode(true);
                    clearInterval(maintenancePollRef.current);
                }
            } catch (err) {
                // Silently ignore poll errors
            }
        };

        maintenancePollRef.current = setInterval(pollMaintenance, 60000);
        return () => clearInterval(maintenancePollRef.current);
    }, []);



    const handleLoginSubmit = async (emailToSubmit, captchaToken) => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await axios.post('/api/requestAccessCode', { email: emailToSubmit, captchaToken });

            trackEvent('AccessCodeRequested', { authMode });
            setEmail(emailToSubmit);
            setResendToken(response.data.resendToken ?? null);
            // Only change the view if we're not already on the verify screen
            if (view !== 'verify') {
                setView('verify');
            }
            setIsLoading(false);
            return true;
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to send access code.');
            setIsLoading(false);
            return false;
        }
    };

    const handleResend = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await axios.post('/api/resendAccessCode', { resendToken });
            trackEvent('AccessCodeResent', { authMode });
            setResendToken(response.data.resendToken ?? null);
            setIsLoading(false);
            return true;
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to resend access code.');
            setIsLoading(false);
            return false;
        }
    };

    const handleVerifySubmit = async (code) => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await axios.post('/api/verifyAccessCode', { email, code });
            trackEvent('AccessCodeVerified', { authMode });
            setAuthenticatedSession(response.data.token);
            if (activeQuestionnaireId) {
                setAppView('survey');
            }
        } catch (err) {
            trackEvent('AccessCodeFailed', { authMode });
            setError(err.response?.data?.message || 'Verification failed.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleLogout = (isSessionExpired = false) => {
        trackEvent('LoggedOut', { authMode, sessionExpired: !!isSessionExpired });
        clearSessionTimers();
        setShowExpiryModal(false);
        window.history.pushState({}, '', window.location.pathname);
        setView('login');
        setIsAuthenticated(false);
        setJwt(null);
        setError(null);
        setAppView('table');
        setActiveQuestionnaireId(null);
        setIsReadOnly(false);

        if (isSessionExpired) {

            setError("Your session has expired. Please log in again.");
        } else {
            setError(null);
        }
    };

    const handleBackToLogin = () => {
        setView('login');
        setError(null);
    };

    const handleOpenQuestionnaire = (id, status) => {
        trackEvent('QuestionnaireOpened', { authMode, readOnly: status === 2 });
        setActiveQuestionnaireId(id);
        setIsReadOnly(status === 2);
        setAppView('survey');
    };

    const handleBackToTable = () => {
        setAppView('table');
        setActiveQuestionnaireId(null);
        setIsReadOnly(false);
    };

    useEffect(() => {
        autoRefreshRef.current = async () => {

            console.log("Embedded mode: Auto-refreshing token 2 mins before expiry...");
            try {
                const api = axios.create({ headers: { Authorization: `Bearer ${jwt}` } });
                const response = await api.post('/api/refreshToken');
                const newToken = response.data.token;

                setJwt(newToken);
                startSessionTimers(newToken, isEmbeddedMode);
                trackEvent('SessionAutoRefreshed', { authMode });
                console.log("Session auto-refreshed successfully.");
            } catch (err) {
                console.error("Failed to auto-refresh session:", err);
                handleLogout(true);
            }
        };
    });




    const renderContent = () => {

        if (maintenanceMode) {
            return <MaintenancePage />;
        }

        if (view === 'loading') {
            return <Spinner animation="border" variant="primary" />;
        }

        if (isAuthenticated) {
            // Check if we're in preview mode
            if (isPreviewMode && previewVersionId) {
                return <QuestionnairePreview
                    jwt={jwt}
                    questionnaireVersionId={previewVersionId}
                    includeDraft={includeDraft}
                />;
            }

            if (appView === 'survey') {
                return <QuestionnaireView
                    jwt={jwt}
                    questionnaireId={activeQuestionnaireId}
                    isReadOnly={isReadOnly}
                    onBack={handleBackToTable}
                    isDynamicsMode={isDynamicsMode}
                    isPortalMode={isPortalMode}
                    wafTokenMap={wafTokenMap}
                />;
            }
            return <QuestionnaireTable
                jwt={jwt}
                onLogout={handleLogout}
                onOpenQuestionnaire={handleOpenQuestionnaire}
                isDynamicsMode={isDynamicsMode}
            />;
        }

        switch (view) {
            case 'verify':
                return <VerifyCode
                    email={email}
                    isLoading={isLoading}
                    error={error}
                    onSubmit={handleVerifySubmit}
                    onBack={handleBackToLogin}
                    onResend={handleResend}
                />;
            case 'login':
            default:
                return <Login isLoading={isLoading} error={error} onSubmit={handleLoginSubmit} termsUrl={termsUrl} hasQuestionnaireId={!!activeQuestionnaireId} recaptchaSiteKey={recaptchaSiteKey} />;
        }
    };

    return (
        <AppInsightsProvider
            connectionString={appInsightsConnectionString}
            authMode={authMode}
            view={view}
            appView={appView}
            isPreviewMode={isPreviewMode}
            userEmail={email}
            isAuthenticated={isAuthenticated}
        >
            <div style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>

                {view === 'authenticated' && (appView === 'survey' || isPreviewMode) ? (
                    renderContent()
                ) : (
                    <Container className="d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
                        {renderContent()}
                    </Container>
                )}
            </div>
            {isEmbeddedMode ? <div></div> : <footer className="survey-footer">
                <div className="survey-footer-info">
                    <p>© ISOQAR Ltd {new Date().getFullYear()} All Rights Reserved Registered in England.</p>
                    <p>Registered Office: Avys House, Heol Crochendy, Parc Nantgarw, Cardiff CF15 7TW. Company Reg No: 09793309</p>
                </div>
                <nav className="survey-footer-nav">
                    <ul>
                        <li><a href="https://www.alcumusgroup.com/privacy-policy">Privacy Policy</a></li>
                        <li><a href="https://isoqar.com/members-area/">Members Area</a></li>
                        <li><a href="https://www.alcumusgroup.com/cookie-policy">Cookie Policy</a></li>
                    </ul>
                </nav>
            </footer>}


            {/* --- Session Expiry Modal --- */}
            <Modal show={showExpiryModal} onHide={() => setShowExpiryModal(false)} centered backdrop="static">
                <Modal.Header>
                    <Modal.Title>Session Expiring Soon</Modal.Title>
                </Modal.Header>
                <Modal.Body>

                    Your session will expire in <strong>{countdown}</strong> seconds. Would you like to extend it?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => handleLogout()}>
                        Log Out
                    </Button>
                    <Button variant="primary" onClick={handleExtendSession}>
                        Extend Session
                    </Button>
                </Modal.Footer>
            </Modal>
        </AppInsightsProvider>
    );
}

export default App;